import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ai from "../../helpers/axios";
import AsyncSelect from "react-select/async";

const AlternateAirportMultiSelect = (props) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [isCalculating, setIsCalculating] = useState(false);

  const loadAirportOptions = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let { data } = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}`);

    let options = [];
    if (data.length >= 1) {
      options = data.map((airport) => ({
        value: airport._id,
        label: `${airport.airportName} (${airport.localCode || airport.code})`,
        airportData: {
          _id: airport._id,
          code: airport.code,
          localCode: airport.localCode,
          airportName: airport.airportName,
        },
      }));
    }

    return options;
  };

  const handleAirportChange = async (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      props.handleChange([]);
      return;
    }

    // If we don't have a current airport ID, we can't calculate distances
    if (!props.currentAirportId) {
      console.warn("No current airport ID provided for distance calculation");
      props.handleChange([]);
      return;
    }

    setIsCalculating(true);

    try {
      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      // Calculate distance for each selected airport
      const alternatesWithDistance = await Promise.all(
        selectedOptions.map(async (option) => {
          try {
            // If airportData doesn't exist, fetch it from the API
            let airportData = option.airportData;
            if (!airportData) {
              const { data: fetchedAirport } = await ai
                .auth(token)
                .get(`/api/airports/${option.value}`);
              airportData = {
                _id: fetchedAirport.id,
                code: fetchedAirport.code,
                localCode: fetchedAirport.localCode,
                airportName: fetchedAirport.airportName,
              };
            }

            const { data } = await ai
              .auth(token)
              .get(
                `/api/airports/calculate-distance?airportId1=${props.currentAirportId}&airportId2=${option.value}`
              );

            return {
              airportId: airportData._id,
              airportCode: airportData.code,
              airportLocalCode: airportData.localCode,
              distance: data.distance,
              // Store display info for the select component
              _displayLabel: `${airportData.localCode || airportData.code} - ${
                data.distance
              } miles`,
            };
          } catch (error) {
            console.error(
              `Error calculating distance for airport ${option.value}:`,
              error
            );
            // Return null for failed entries so we can filter them out
            return null;
          }
        })
      );

      // Filter out any null entries (failed calculations)
      const validAlternates = alternatesWithDistance.filter(
        (alt) => alt !== null
      );

      props.handleChange(validAlternates);
    } catch (error) {
      console.error("Error processing alternate airports:", error);
    } finally {
      setIsCalculating(false);
    }
  };

  // Format the current value for display in the select component
  const formatValueForSelect = () => {
    if (!props.selectedAirports || props.selectedAirports.length === 0) {
      return null;
    }

    return props.selectedAirports.map((airport) => ({
      value: airport.airportId,
      label:
        airport._displayLabel ||
        `${airport.airportLocalCode || airport.airportCode} - ${
          airport.distance
        } miles`,
    }));
  };

  return (
    <div className="form-group">
      <label>
        {props.label || "Alternate Airports"}
        {isCalculating && (
          <span style={{ marginLeft: "10px", fontSize: "12px", color: "#666" }}>
            Calculating distances...
          </span>
        )}
      </label>

      <AsyncSelect
        className="alternate-airport-select"
        classNamePrefix="select"
        loadOptions={loadAirportOptions}
        placeholder={props.placeholder || "Select Alternate Airports"}
        onChange={handleAirportChange}
        isClearable={true}
        isMulti={true}
        cacheOptions
        value={formatValueForSelect()}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        isDisabled={!props.currentAirportId || isCalculating}
      ></AsyncSelect>

      {!props.currentAirportId && (
        <small style={{ color: "#999", fontSize: "12px" }}>
          Save the airport first to enable alternate airport selection
        </small>
      )}
    </div>
  );
};

export default AlternateAirportMultiSelect;
